import {observable, action, computed, runInAction} from 'mobx';
import {getRootStore} from '../../../app.bootstrap';
import {ITranslate} from '../../../services/LocalizationService';
import {ScanStatus} from '../../../services/AngularServices/WifiAnalyzerService';
import {ReportType} from '../../../services/AngularServices/WifiAnalyzerService';

export interface IPollingBirthCertificateReportModalController {
    readonly translate: ITranslate;
    readonly isVisible: boolean;
    readonly scanId: string | null;
    readonly reportType: ReportType | null;
    readonly error: boolean;

    show(scanId: string, reportType: ReportType): void;
    hide(): void;
    setError(value: boolean): void;
    download(): Promise<void>;
}

export class PollingBirthCertificateReportModalController implements IPollingBirthCertificateReportModalController {
    private readonly _translate: ITranslate;

    @observable private _isVisible: boolean = false;
    @observable private _scanId: string | null = null;
    @observable private _cancelled: boolean = false;
    @observable private _error: boolean = false;
    @observable private _reportType: ReportType | null = null;

    constructor(translate: ITranslate) {
        this._translate = translate;

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this.setError = this.setError.bind(this);
        this.download = this.download.bind(this);
    }

    get translate(): ITranslate {
        return this._translate;
    }

    @action
    show(scanId: string, reportType: ReportType): void {
        this._scanId = scanId;
        this._reportType = reportType;
        this._isVisible = true;
        this._cancelled = false;
        this._error = false;
    }

    @action
    hide(): void {
        this._scanId = null;
        this._isVisible = false;
        this._cancelled = true;
        this._error = false;
        this._reportType = null;
    }

    @action
    setError(value: boolean): void {
        this._error = value;
    }

    @computed
    get isVisible(): boolean {
        return this._isVisible;
    }

    @computed
    get scanId(): string | null {
        return this._scanId;
    }

    @computed
    get error(): boolean {
        return this._error;
    }

    @computed
    get reportType(): ReportType | null {
        return this._reportType;
    }

    async download(): Promise<void> {
        if (!this._scanId || !this._reportType) {
            return;
        }

        try {
            const result = await getRootStore().wifiAnalyzerService.triggerReportGeneration(
                this._scanId,
                this._reportType
            );

            if (this._cancelled) {
                return;
            }

            if (result.status === ScanStatus.READY && result.url) {
                this.hide();
                window.open(`${result.url}`, '_blank');
                return;
            }

            const url = await getRootStore().wifiAnalyzerService.pollUntilReady(
                this._scanId,
                this._reportType,
                () => this._cancelled
            );

            if (!this._cancelled) {
                this.hide();
                window.open(`${url}`, '_blank');
            }
        } catch (err) {
            if (!this._cancelled) {
                runInAction(() => {
                    this._error = true;
                });
            }
        }
    }
}
